import type { SelectedDocument } from '@athena/ui'
import { useCallback, useState } from 'react'

import { looksLikePdf, screenPdfFiles, type RejectedPdf } from './pdfSelection.js'

export interface ChosenPdf extends SelectedDocument {
  /** The file itself. Base64'd and sent when a reading runs; never stored. */
  file: File
}

export interface PdfSelection {
  documents: readonly ChosenPdf[]
  /** The files turned away by the most recent choice, and why. */
  rejected: readonly RejectedPdf[]
  /** Screens, checks the signature and appends. Keeps the order given. */
  add: (files: readonly File[]) => Promise<void>
  remove: (id: string) => void
  clear: () => void
}

/**
 * The PDFs a tutor has chosen.
 *
 * The sibling of `useImageSelection`, and much shorter, because a PDF needs
 * none of what that hook exists for: there is no thumbnail, so there is no
 * object URL, so there is nothing to revoke. The `File` objects are held as
 * they were given and read once, when a reading runs.
 *
 * Nothing here opens a PDF. The browser checks that the file starts with
 * `%PDF-` and stops there — reading the text is the server's job, and it is
 * the server that has the parser and the authority.
 */
export function usePdfSelection(): PdfSelection {
  const [chosen, setChosen] = useState<readonly ChosenPdf[]>([])
  const [rejected, setRejected] = useState<readonly RejectedPdf[]>([])

  const add = useCallback(
    async (files: readonly File[]) => {
      // Against the selection on screen: the file picker and a drop each fire
      // once, so this is the count — and the weight — the tutor is looking at.
      const screened = screenPdfFiles(
        files,
        chosen.length,
        chosen.reduce((sum, document) => sum + document.bytes, 0),
      )

      const accepted: ChosenPdf[] = []
      const wrongBytes: RejectedPdf[] = []
      for (const file of screened.accepted) {
        if (await looksLikePdf(file)) {
          accepted.push({
            id: crypto.randomUUID(),
            name: file.name,
            bytes: file.size,
            file,
          })
        } else {
          // Named as a type problem, because that is what it is: the file
          // claimed to be a PDF and its first five bytes say otherwise.
          wrongBytes.push({ name: file.name, reason: 'type' })
        }
      }

      setChosen((current) => [...current, ...accepted])
      setRejected([...screened.rejected, ...wrongBytes])
    },
    [chosen],
  )

  const remove = useCallback((id: string) => {
    setChosen((current) => current.filter((document) => document.id !== id))
  }, [])

  const clear = useCallback(() => {
    setChosen([])
    setRejected([])
  }, [])

  return { documents: chosen, rejected, add, remove, clear }
}
